import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchUppdrag, UppdragQueryKey } from "api/uppdrag.api";
import { UppdragStatusTag } from "components/UppdragStatusTag";
import { useNotification } from "hooks/useNotification";
import { Uppdrag, UppdragStatus } from "types/uppdrag.type";

type Props = {
  uppdrag: Uppdrag;
};
export const UppdragStatusButton = ({ uppdrag }: Props) => {
  const qc = useQueryClient();
  const notification = useNotification();
  const mutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
    },
    onError: () => {
      notification.error();
    },
  });

  return (
    <Dropdown
      selectedStatus={uppdrag.status}
      onClick={(newStatus: UppdragStatus) =>
        mutation.mutate({ id: uppdrag.id, payload: { status: newStatus } })
      }
      uppdrag={uppdrag}
    />
  );
};

type DropdownProps = {
  selectedStatus: UppdragStatus;
  onClick: (newStatus: UppdragStatus) => void;
  uppdrag: Uppdrag;
};
const Dropdown = ({ selectedStatus, onClick, uppdrag }: DropdownProps) => {
  // Status can only change once an operator owns the uppdrag.
  const isUnassigned = !uppdrag.assignedToUser;
  const tooltipId = `uppdrag-status-disabled-${uppdrag.id}`;

  return (
    <Menu as="div" className="relative inline-block text-left sm:mb-2">
      {/* Tooltip lives on the wrapper: a disabled button doesn't fire hover
          events, so the explanation must come from an enabled element. */}
      <div
        data-tooltip-id={isUnassigned ? tooltipId : undefined}
        data-tooltip-content={
          isUnassigned
            ? "Tilldela en operatör för att kunna påbörja uppdraget"
            : undefined
        }
        data-tooltip-place="top"
      >
        <MenuButton
          disabled={isUnassigned}
          className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-100 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
        >
          <UppdragStatusTag status={selectedStatus} />
          <ChevronDownIcon
            aria-hidden="true"
            className="-mr-1 size-5 text-gray-400"
          />
        </MenuButton>
      </div>
      {isUnassigned && <Tooltip id={tooltipId} />}

      <MenuItems
        transition
        className="ring-black/5 absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="py-1">
          <MenuItem>
            <button
              onClick={() => onClick(UppdragStatus.Scheduled)}
              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
            >
              <UppdragStatusTag status={UppdragStatus.Scheduled} />
            </button>
          </MenuItem>
          <MenuItem>
            <button
              onClick={() => onClick(UppdragStatus.Started)}
              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
            >
              <UppdragStatusTag status={UppdragStatus.Started} />
            </button>
          </MenuItem>
          <MenuItem>
            <button
              onClick={() => onClick(UppdragStatus.Completed)}
              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
              disabled={!uppdrag.areAllUppgiftsCompleted}
            >
              <UppdragStatusTag
                status={UppdragStatus.Completed}
                disabled={
                  !uppdrag.areAllUppgiftsCompleted && !!uppdrag.uppgiftCount
                } // Man ska kunna klarmarkera ett uppdrag utan uppgifter
              />
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  );
};
